import React from "react";
import DataTable from "react-data-table-component";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminService } from "./adminApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, RefreshCcw, User as UserIcon, Plus, Edit, X, ChevronDown, ChevronUp, Filter } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";

const userSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  phone_number: z.string().optional(),
  user_type: z.enum(["CUSTOMER", "INTERNAL", "BAP", "LE"]),
  is_staff: z.boolean().default(false),
  bap_id: z.string().optional().nullable(),
  roles: z.array(z.string()).default([]),
});

type UserFormValues = z.infer<typeof userSchema>;

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  is_staff: boolean;
  is_superuser: boolean;
  business_name: string | null;
  bap_name: string | null;
  organisation: string | null;
  is_whitespace: boolean;
  journey_stage: string;
  journey_updated_at: string | null;
  user_type?: string;
  bap_id?: number | null;
  roles?: string[];
}

interface FilterState {
  userType: string;
  isStaff: string;
  isSuperuser: string;
  organisation: string;
  role: string;
}

const defaultFilters: FilterState = {
  userType: "all",
  isStaff: "all",
  isSuperuser: "all",
  organisation: "",
  role: "all",
};

const UserManagement = () => {
  const [filterText, setFilterText] = React.useState("");
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<User | null>(null);
  const [showFilters, setShowFilters] = React.useState(false);
  const [filters, setFilters] = React.useState<FilterState>(defaultFilters);

  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const response = await adminService.getUsers();
      return response.data as User[];
    },
  });

  const { data: refData } = useQuery({
    queryKey: ["admin-reference-data"],
    queryFn: async () => {
      const response = await adminService.getReferenceData();
      return response.data;
    },
  });

  const baps = refData?.baps || [];

  const createMutation = useMutation({
    mutationFn: (userData: UserFormValues) => adminService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("User created successfully");
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to create user");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (userData: UserFormValues & { id: number }) => adminService.updateUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("User updated successfully");
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to update user");
    },
  });

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      username: "",
      first_name: "",
      last_name: "",
      phone_number: "",
      user_type: "CUSTOMER",
      is_staff: false,
      bap_id: null,
      roles: [],
    },
  });

  React.useEffect(() => {
    if (editingUser) {
      form.reset({
        username: editingUser.username,
        first_name: editingUser.first_name || "",
        last_name: editingUser.last_name || "",
        phone_number: editingUser.phone_number || "",
        user_type: (editingUser.user_type as any) || "CUSTOMER",
        is_staff: editingUser.is_staff,
        bap_id: editingUser.bap_id ? String(editingUser.bap_id) : null,
        roles: editingUser.roles || [],
      });
    } else {
      form.reset({
        username: "",
        first_name: "",
        last_name: "",
        phone_number: "",
        user_type: "CUSTOMER",
        is_staff: false,
        bap_id: null,
        roles: [],
      });
    }
  }, [editingUser, form]);

  const onSubmit = (values: UserFormValues) => {
    if (editingUser) {
      updateMutation.mutate({ ...values, id: editingUser.id });
    } else {
      createMutation.mutate(values);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setFilterText("");
  };

  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filterText) count++;
    if (filters.userType !== "all") count++;
    if (filters.isStaff !== "all") count++;
    if (filters.isSuperuser !== "all") count++;
    if (filters.organisation) count++;
    if (filters.role !== "all") count++;
    return count;
  }, [filterText, filters]);

  const filteredItems = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((item) => {
      // Text search
      const textMatch =
        !filterText ||
        (item.username && item.username.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.first_name && item.first_name.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.last_name && item.last_name.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.organisation && item.organisation.toLowerCase().includes(filterText.toLowerCase()));

      // User type filter
      const userTypeMatch =
        filters.userType === "all" ||
        (item.user_type && item.user_type === filters.userType);

      // Staff filter
      const staffMatch =
        filters.isStaff === "all" ||
        (filters.isStaff === "yes" ? item.is_staff : !item.is_staff);

      // Superuser filter
      const superuserMatch =
        filters.isSuperuser === "all" ||
        (filters.isSuperuser === "yes" ? item.is_superuser : !item.is_superuser);

      // Organisation filter
      const orgMatch =
        !filters.organisation ||
        (item.organisation &&
          item.organisation.toLowerCase().includes(filters.organisation.toLowerCase()));

      // Role filter
      const roleMatch =
        filters.role === "all" ||
        (item.roles && item.roles.includes(filters.role));

      return textMatch && userTypeMatch && staffMatch && superuserMatch && orgMatch && roleMatch;
    });
  }, [data, filterText, filters]);

  const columns = [
    {
      name: "ID",
      selector: (row: User) => row.id,
      sortable: true,
      width: "70px",
    },
    {
      name: "Username",
      selector: (row: User) => row.username,
      sortable: true,
    },
    {
      name: "Full Name",
      selector: (row: User) => `${row.first_name || ""} ${row.last_name || ""}`.trim() || "-",
      sortable: true,
    },
    {
      name: "Organisation",
      selector: (row: User) => row.organisation || "-",
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row: User) => row.phone_number || "-",
      sortable: true,
    },
    {
      name: "User Type",
      cell: (row: User) =>
        row.user_type ? (
          <Badge variant="outline" className="text-xs font-medium">
            {row.user_type}
          </Badge>
        ) : (
          <span className="text-gray-400">-</span>
        ),
      sortable: true,
      selector: (row: User) => row.user_type || "",
      width: "110px",
    },
    {
      name: "Roles",
      selector: (row: User) => row.roles?.join(", ") || "-",
      sortable: true,
    },
    {
      name: "Staff",
      cell: (row: User) =>
        row.is_staff ? (
          <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">Yes</Badge>
        ) : (
          <span className="text-gray-400 text-xs">No</span>
        ),
      selector: (row: User) => (row.is_staff ? "Yes" : "No"),
      sortable: true,
      width: "80px",
    },
    {
      name: "Superuser",
      cell: (row: User) =>
        row.is_superuser ? (
          <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">Yes</Badge>
        ) : (
          <span className="text-gray-400 text-xs">No</span>
        ),
      selector: (row: User) => (row.is_superuser ? "Yes" : "No"),
      sortable: true,
      width: "100px",
    },
    {
      name: "Actions",
      cell: (row: User) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleEdit(row)}
          className="h-8 w-8 p-0 hover:bg-intellend-green/10 hover:text-intellend-green"
          title="Edit user"
        >
          <Edit className="h-4 w-4" />
        </Button>
      ),
      width: "80px",
      ignoreRowClick: true,
    },
  ];

  const customStyles = {
    header: {
      style: {
        minHeight: "56px",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f9fafb",
        borderTopStyle: "solid",
        borderTopWidth: "1px",
        borderTopColor: "#e5e7eb",
      },
    },
    headCells: {
      style: {
        fontWeight: "600",
        color: "#374151",
        fontSize: "0.875rem",
      },
    },
    cells: {
      style: {
        fontSize: "0.875rem",
        color: "#4b5563",
      },
    },
    rows: {
      style: {
        "&:hover": {
          backgroundColor: "#f0fdf4",
        },
      },
    },
  };

  const availableRoles: string[] = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    const roleSet = new Set<string>();
    data.forEach((u) => u.roles?.forEach((r) => roleSet.add(r)));
    return Array.from(roleSet).sort();
  }, [data]);

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-full rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        {/* Header */}
        <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <UserIcon className="h-6 w-6 text-intellend-green" />
              Users
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage and view all registered users across the platform.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {/* Search input */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search users..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="pl-9 pr-8"
              />
              {filterText && (
                <button
                  onClick={() => setFilterText("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Filter toggle button */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowFilters((v) => !v)}
              className={`flex items-center gap-1.5 relative ${
                activeFilterCount > 0
                  ? "border-intellend-green text-intellend-green bg-intellend-green/5"
                  : ""
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center rounded-full bg-intellend-green text-white text-[10px] font-bold w-4 h-4">
                  {activeFilterCount}
                </span>
              )}
              {showFilters ? (
                <ChevronUp className="h-3.5 w-3.5 ml-0.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 ml-0.5" />
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="flex items-center gap-2"
            >
              <RefreshCcw className="h-4 w-4" /> Refresh
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAdd}
              className="flex items-center gap-2 bg-intellend-green hover:bg-intellend-green/90"
            >
              <Plus className="h-4 w-4" /> Add User
            </Button>
          </div>
        </div>

        {/* Expandable Filter Panel */}
        {showFilters && (
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4 animate-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <Filter className="h-4 w-4 text-intellend-green" />
                Advanced Filters
              </span>
              {activeFilterCount > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                >
                  <X className="h-3 w-3 mr-1" /> Clear all
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {/* User Type */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">User Type</label>
                <Select
                  value={filters.userType}
                  onValueChange={(v) => setFilters((f) => ({ ...f, userType: v }))}
                >
                  <SelectTrigger className="h-8 text-xs bg-white">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="CUSTOMER">Customer</SelectItem>
                    <SelectItem value="INTERNAL">Internal</SelectItem>
                    <SelectItem value="BAP">BAP</SelectItem>
                    <SelectItem value="LE">LE</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Staff */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Staff</label>
                <Select
                  value={filters.isStaff}
                  onValueChange={(v) => setFilters((f) => ({ ...f, isStaff: v }))}
                >
                  <SelectTrigger className="h-8 text-xs bg-white">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="yes">Staff only</SelectItem>
                    <SelectItem value="no">Non-staff only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Superuser */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Superuser</label>
                <Select
                  value={filters.isSuperuser}
                  onValueChange={(v) => setFilters((f) => ({ ...f, isSuperuser: v }))}
                >
                  <SelectTrigger className="h-8 text-xs bg-white">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="yes">Superusers only</SelectItem>
                    <SelectItem value="no">Non-superusers only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Role */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Role</label>
                <Select
                  value={filters.role}
                  onValueChange={(v) => setFilters((f) => ({ ...f, role: v }))}
                >
                  <SelectTrigger className="h-8 text-xs bg-white">
                    <SelectValue placeholder="All roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All roles</SelectItem>
                    {availableRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                    {(refData?.roles || [])
                      .filter((r: any) => !availableRoles.includes(r.name))
                      .map((role: any) => (
                        <SelectItem key={role.id} value={role.name}>
                          {role.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Organisation */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Organisation</label>
                <div className="relative">
                  <Input
                    placeholder="Filter by org..."
                    value={filters.organisation}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, organisation: e.target.value }))
                    }
                    className="h-8 text-xs bg-white pr-7"
                  />
                  {filters.organisation && (
                    <button
                      onClick={() => setFilters((f) => ({ ...f, organisation: "" }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Active filter badges */}
            {activeFilterCount > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {filterText && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-intellend-green/10 text-intellend-green text-xs px-2 py-0.5 font-medium">
                    Search: "{filterText}"
                    <button onClick={() => setFilterText("")}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filters.userType !== "all" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-intellend-green/10 text-intellend-green text-xs px-2 py-0.5 font-medium">
                    Type: {filters.userType}
                    <button onClick={() => setFilters((f) => ({ ...f, userType: "all" }))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filters.isStaff !== "all" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-intellend-green/10 text-intellend-green text-xs px-2 py-0.5 font-medium">
                    Staff: {filters.isStaff === "yes" ? "Yes" : "No"}
                    <button onClick={() => setFilters((f) => ({ ...f, isStaff: "all" }))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filters.isSuperuser !== "all" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-intellend-green/10 text-intellend-green text-xs px-2 py-0.5 font-medium">
                    Superuser: {filters.isSuperuser === "yes" ? "Yes" : "No"}
                    <button onClick={() => setFilters((f) => ({ ...f, isSuperuser: "all" }))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filters.role !== "all" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-intellend-green/10 text-intellend-green text-xs px-2 py-0.5 font-medium">
                    Role: {filters.role}
                    <button onClick={() => setFilters((f) => ({ ...f, role: "all" }))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filters.organisation && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-intellend-green/10 text-intellend-green text-xs px-2 py-0.5 font-medium">
                    Org: "{filters.organisation}"
                    <button onClick={() => setFilters((f) => ({ ...f, organisation: "" }))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Results count */}
        {!isLoading && (
          <div className="mb-2 text-xs text-gray-500">
            Showing <span className="font-semibold text-gray-700">{filteredItems.length}</span> of{" "}
            <span className="font-semibold text-gray-700">{data?.length ?? 0}</span> users
          </div>
        )}

        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <DataTable
            columns={columns as any}
            data={filteredItems || []}
            pagination
            progressPending={isLoading}
            progressComponent={
              <div className="py-20 text-center text-sm text-gray-500">Loading users...</div>
            }
            noDataComponent={
              <div className="py-20 text-center text-sm text-gray-500">
                {activeFilterCount > 0
                  ? "No users match the current filters."
                  : "No users found."}
              </div>
            }
            customStyles={customStyles}
            highlightOnHover
            pointerOnHover
            paginationPerPage={25}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            onRowClicked={(row) => handleEdit(row)}
          />
        </div>
        {isError && (
          <div className="mt-4 p-4 rounded-lg bg-red-50 text-red-600 text-sm border border-red-100">
            Unable to load user list. Please try again later.
          </div>
        )}
      </div>

      {/* Edit / Add User Dialog */}
      <Dialog
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) setEditingUser(null);
        }}
      >
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingUser ? (
                <>
                  <Edit className="h-4 w-4 text-intellend-green" />
                  Edit User
                  <span className="ml-1 text-sm font-normal text-gray-500">
                    — {editingUser.username}
                  </span>
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 text-intellend-green" />
                  Add New User
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-2">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="username"
                        {...field}
                        disabled={!!editingUser}
                        className={editingUser ? "bg-gray-50 text-gray-500" : ""}
                      />
                    </FormControl>
                    {editingUser && (
                      <p className="text-xs text-gray-400">Username cannot be changed</p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="First name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="phone_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Phone number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="user_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>User Type *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select user type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="CUSTOMER">Customer</SelectItem>
                        <SelectItem value="INTERNAL">Internal</SelectItem>
                        <SelectItem value="BAP">BAP User</SelectItem>
                        <SelectItem value="LE">LE User</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("user_type") === "BAP" && (
                <FormField
                  control={form.control}
                  name="bap_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assign to BAP</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value || undefined}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select BAP" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {baps.map((bap: any) => (
                            <SelectItem key={bap.id} value={String(bap.id)}>
                              {bap.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <div className="space-y-2">
                <FormLabel>Roles</FormLabel>
                <div className="grid grid-cols-2 gap-2">
                  {(refData?.roles || []).map((role: any) => (
                    <FormField
                      key={role.id}
                      control={form.control}
                      name="roles"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={role.id}
                            className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-2 shadow-sm"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(role.name)}
                                onCheckedChange={(checked) => {
                                  const currentValues = Array.isArray(field.value)
                                    ? field.value
                                    : [];
                                  return checked
                                    ? field.onChange([...currentValues, role.name])
                                    : field.onChange(
                                        currentValues.filter(
                                          (value: string) => value !== role.name
                                        )
                                      );
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              {role.name}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-4 items-center pt-2">
                <FormField
                  control={form.control}
                  name="is_staff"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm flex-1 gap-2">
                      <div className="space-y-0.5">
                        <FormLabel>Staff</FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm flex-1 gap-2 bg-gray-50">
                  <div className="space-y-0.5">
                    <FormLabel className="text-gray-500">Superuser</FormLabel>
                  </div>
                  <Switch
                    checked={editingUser?.is_superuser || false}
                    disabled
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingUser(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="bg-intellend-green hover:bg-intellend-green/90"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? editingUser
                      ? "Updating..."
                      : "Creating..."
                    : editingUser
                    ? "Update User"
                    : "Create User"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
